import { 
  rowHasMappedKey,
  trimUpdateValue,
  checkDuplicateKeyInBulkUpdate,
  parseCurrency,
  parsePercent,
} from './sharedImportHelpers';

export const getMaterialByExactName = ({
  name,
  map,
  errorMessage = 'Item not found',
}) => {
  const item = map?.[name];
  if (!item) {
    throw new Error(errorMessage);
  }

  return item;
};

const getMaterialLocation = ({
  material,
  globalLocation,
  equipmentLocation,
}) => {
  if (globalLocation) {
    return material.locations?.find((loc) => (
      loc.globalMaterialLocationsId === globalLocation.id
    ));
  }

  return material.locations?.find((loc) => (
    loc.equipmentId === equipmentLocation.id
  ));
};

const updateLocationPayload = ({
  material,
  location,
  quantity,
  payload,
}) => {
  const materialLocation = getMaterialLocation({
    material,
    globalLocation: location.globalLocation,
    equipmentLocation: location.equipmentLocation,
  });

  if (materialLocation) {
    payload.updatedLocations = [{
      id: materialLocation.id,
      equipmentId: location.equipmentLocation?.id,
      globalMaterialLocationsId: location.globalLocation?.id,
      quantity,
      quantityAllocated: materialLocation.quantityAllocated ?? 0,
    }];
    return;
  }

  payload.createdLocations = [{
    equipmentId: location.equipmentLocation?.id,
    globalMaterialLocationsId: location.globalLocation?.id,
    quantity,
    quantityAllocated: 0,
  }];
};

const applyLocationQuantityUpdate = (row, context, material, payload) => {
  const quantity = Number.parseFloat(row.locationQuantity);
  const locationName = trimUpdateValue(row.location);
  const defaultGlobalLocation = context.globalMaterialLocationNameMap?.DEFAULT;

  if (locationName) {
    const location = {
      globalLocation: context.globalMaterialLocationNameMap?.[locationName],
      equipmentLocation: context.equipmentMap?.[locationName],
    };

    if (location.globalLocation || location.equipmentLocation) {
      updateLocationPayload({
        material,
        location,
        quantity,
        payload,
      });
      return;
    }

    payload.importedLocations = [{
      locationText: locationName,
      quantity,
      quantityAllocated: 0,
    }];
    return;
  }

  if (defaultGlobalLocation) {
    updateLocationPayload({
      material,
      location: {
        globalLocation: defaultGlobalLocation,
      },
      quantity,
      payload,
    });
    return;
  }

  throw new Error('Default material location not found');
};

export const buildUpdateMaterialImportPayload = (row, context) => {
  const name = trimUpdateValue(row.name);
  if (!name) {
    throw new Error('Name is required');
  }

  const material = getMaterialByExactName({ 
    name, 
    map: context.materialMap, 
    errorMessage: `Material "${name}" not found`,
  });
  
  checkDuplicateKeyInBulkUpdate({
    key: name,
    label: 'Name',
    seen: context.bulkMaterialUpdateSeenNames,
  });

  const payload = {}

  if (rowHasMappedKey(row, 'cost')) {
    payload.cost = parseCurrency(row.cost);
  }

  if (rowHasMappedKey(row, 'markup')) {
    payload.markup = parsePercent(row.markup);
  }

  if (rowHasMappedKey(row, 'labourCost')) {
    payload.labourCost = parseCurrency(row.labourCost);
  }

  if (rowHasMappedKey(row, 'labourMarkup')) {
    payload.labourMarkup = parsePercent(row.labourMarkup);
  }

  if (rowHasMappedKey(row, 'units')) {
    const unitsValue = trimUpdateValue(row.units);
    if (!unitsValue) {
      throw new Error('Units cannot be empty');
    }
    payload.units = unitsValue;
  }

  if (rowHasMappedKey(row, 'locationQuantity')) {
    applyLocationQuantityUpdate(row, context, material, payload);
  }

  if (Object.keys(payload).length === 0) {
    throw new Error('No updatable fields in mapped columns');
  }

  return { updateId: material.id, payload };
}